import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Edit2, Trash2, MoreVertical, Settings, Monitor, Clock, CheckSquare, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  ServiceOrderWithTasks
} from '../../types/serviceOrder';
import { formatDate } from '../../utils/dateUtils';

interface ServiceOrderSummaryCardProps {
  serviceOrder: ServiceOrderWithTasks;
  projectId: string;
  onEdit?: (serviceOrder: ServiceOrderWithTasks) => void;
  onDelete?: (serviceOrderId: string) => void;
  hoursTrackingEnabled?: boolean;
}

export const ServiceOrderSummaryCard: React.FC<ServiceOrderSummaryCardProps> = ({
  serviceOrder,
  projectId,
  onEdit,
  onDelete,
  hoursTrackingEnabled
}) => {
  const navigate = useNavigate();
  const [showActions, setShowActions] = useState(false);


  const handleManageClick = () => {
    navigate(`/projects/${projectId}/service-orders/${serviceOrder._id}`);
  };

  const handlePresentationClick = () => {
    navigate(`/projects/${projectId}/service-orders/${serviceOrder._id}/presentation`);
  };

  const calculateProgress = () => {
    if (serviceOrder.totalTasks === 0) return 0;
    const completedTasks = serviceOrder.tasks.filter(task => task.status === 'encerrada').length;
    return Math.round((completedTasks / serviceOrder.totalTasks) * 100);
  };

  const calculateMappedHours = () => {
    return serviceOrder.tasks.reduce((total, task) => total + task.horasEstimadas, 0);
  };

  const calculateMappedTasks = () => {
    return serviceOrder.totalTasks;
  };

  const progress = calculateProgress();
  const mappedHours = calculateMappedHours();
  const mappedTasks = calculateMappedTasks();


  return (
    <Card variant="glass" className="relative group hover:shadow-lg hover:shadow-white/5 transition-all">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg text-foreground-primary mb-2">
              {serviceOrder.name}
            </CardTitle>
            
            {/* Period */}
            <div className="flex items-center gap-2 text-sm text-foreground-tertiary mb-3 flex-wrap">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(serviceOrder.startDate, { format: 'short' })} - {formatDate(serviceOrder.endDate, { format: 'short' })}
              </div>

              {/* Billing Period Badge - Only if hours tracking enabled */}
              {hoursTrackingEnabled && serviceOrder.billingPeriodLabel && (
                <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-300 border-blue-500/30">
                  Débito: {serviceOrder.billingPeriodLabel}
                </Badge>
              )}
            </div>

            {/* Description with background */}
            {serviceOrder.description && (
              <div className="bg-white/5 rounded-lg p-3 mb-3">
                <p className="text-sm text-foreground-secondary line-clamp-2">
                  {serviceOrder.description}
                </p>
              </div>
            )}
          </div>

          {/* Action menu */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowActions(!showActions)}
              className="text-foreground-tertiary hover:text-foreground-primary"
            >
              <MoreVertical className="w-4 h-4" />
            </Button>

            {showActions && (
              <div className="absolute right-0 top-8 z-10 w-48 bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-lg shadow-xl py-2">
                <button
                  onClick={() => {
                    handlePresentationClick();
                    setShowActions(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-foreground-secondary hover:text-foreground-primary hover:bg-white/5 rounded flex items-center gap-2"
                >
                  <Monitor className="w-3 h-3" />
                  Visualização
                </button>
                <button
                  onClick={() => {
                    handleManageClick();
                    setShowActions(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-foreground-secondary hover:text-foreground-primary hover:bg-white/5 rounded flex items-center gap-2"
                >
                  <Settings className="w-3 h-3" />
                  Gerenciar
                </button>
                {onEdit && (
                  <button
                    onClick={() => {
                      onEdit(serviceOrder);
                      setShowActions(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-foreground-secondary hover:text-foreground-primary hover:bg-white/5 rounded flex items-center gap-2"
                  >
                    <Edit2 className="w-3 h-3" />
                    Editar
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => {
                      onDelete(serviceOrder._id);
                      setShowActions(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded flex items-center gap-2"
                  >
                    <Trash2 className="w-3 h-3" />
                    Excluir
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex justify-between text-xs text-foreground-tertiary mb-1">
            <span>Progresso das Tarefas</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* KPIs Section */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
          {/* Horas Mapeadas */}
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Clock className="w-4 h-4 text-blue-400 mr-1" />
              <span className="text-xs text-foreground-tertiary">Horas</span>
            </div>
            <div className="text-lg font-bold text-foreground-primary">
              {mappedHours}h
            </div>
          </div>

          {/* Tarefas Mapeadas */}
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <CheckSquare className="w-4 h-4 text-green-400 mr-1" />
              <span className="text-xs text-foreground-tertiary">Tarefas</span>
            </div>
            <div className="text-lg font-bold text-foreground-primary">
              {mappedTasks}
            </div>
          </div>

          {/* Progresso */}
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <TrendingUp className="w-4 h-4 text-purple-400 mr-1" />
              <span className="text-xs text-foreground-tertiary">Progresso</span>
            </div>
            <div className="text-lg font-bold text-foreground-primary">
              {progress}%
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-2 pt-4 border-t border-white/10">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePresentationClick}
            className="flex-1 border-white/20 hover:bg-white/10"
          >
            <Monitor className="w-4 h-4 mr-2" />
            Visualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleManageClick}
            className="flex-1 border-white/20 hover:bg-white/10"
          >
            <Settings className="w-4 h-4 mr-2" />
            Gerenciar
          </Button>
        </div>
      </CardContent>

      {/* Overlay para fechar menu */}
      {showActions && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setShowActions(false)}
        />
      )}
    </Card>
  );
};